import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Lock, Mail, Save, Send } from 'lucide-react';
import { toast } from 'sonner';

const FELD = {
  width: '100%',
  border: '1px solid #E2E8F0',
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 13,
  color: '#0F172A',
  background: '#fff',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const BESCHRIFTUNG = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#334155',
  marginBottom: 5,
};

const HINWEIS = { fontSize: 11, color: '#94A3B8', margin: '4px 0 0' };

function Knopf({ children, onClick, disabled, variante = 'schlicht', icon: Icon }) {
  const haupt = variante === 'haupt';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        background: haupt ? '#E07000' : '#fff',
        color: haupt ? '#fff' : '#334155',
        border: haupt ? '1px solid #E07000' : '1px solid #E2E8F0',
        borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
      }}
    >
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
}

/**
 * Zugangsdaten und Registrierungs-Link der Einladungsmail.
 *
 * Das Postfachpasswort wird nur gesendet, nie geladen: Der Server meldet
 * ausschliesslich, ob eines hinterlegt ist. Ein leeres Feld heisst deshalb
 * "unveraendert lassen", nicht "loeschen".
 */
export default function EinladungEinstellungen({ apiBase, session, onGespeichert }) {
  const [stand, setStand] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [testet, setTestet] = useState(false);
  const [fehler, setFehler] = useState('');

  const [registrierungUrl, setRegistrierungUrl] = useState('');
  const [absender, setAbsender] = useState('');
  const [smtpBenutzer, setSmtpBenutzer] = useState('');
  const [smtpPasswort, setSmtpPasswort] = useState('');
  const [testEmpfaenger, setTestEmpfaenger] = useState('');

  const authHeaders = useMemo(() => (
    session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
  ), [session]);

  const uebernehmen = useCallback((daten) => {
    setStand(daten);
    setRegistrierungUrl(daten?.einstellungen?.registrierungUrl || '');
    setAbsender(daten?.einstellungen?.absender || '');
    setSmtpBenutzer(daten?.einstellungen?.smtpBenutzer || '');
    setSmtpPasswort('');
  }, []);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      const antwort = await axios.get(`${apiBase}/automation/vermittler-einstellungen`, { headers: authHeaders });
      uebernehmen(antwort.data);
    } catch (error) {
      setFehler(error?.response?.data?.detail || error.message || 'Einstellungen konnten nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [apiBase, authHeaders, uebernehmen]);

  useEffect(() => { laden(); }, [laden]);

  useEffect(() => {
    if (!testEmpfaenger && session?.user?.email) setTestEmpfaenger(session.user.email);
  }, [session, testEmpfaenger]);

  const speichern = async () => {
    setSpeichert(true);
    try {
      const antwort = await axios.put(`${apiBase}/automation/vermittler-einstellungen`, {
        registrierungUrl,
        absender,
        smtpBenutzer,
        // Leer heisst: vorhandenes Passwort behalten.
        ...(smtpPasswort ? { smtpPasswort } : {}),
      }, { headers: authHeaders });
      uebernehmen(antwort.data);
      toast.success('Gespeichert.');
      if (onGespeichert) onGespeichert();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  };

  const testen = async (art) => {
    setTestet(art);
    try {
      const antwort = await axios.post(`${apiBase}/automation/vermittler-einstellungen`, {
        aktion: 'test',
        testArt: art,
        testEmpfaenger,
        testName: 'Testempfänger',
      }, { headers: authHeaders });
      toast.success(antwort.data?.message || 'Testmail versendet.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Testmail fehlgeschlagen.');
    } finally {
      setTestet(false);
    }
  };

  const einsatzbereit = stand?.einsatzbereit === true;
  const passwortDa = stand?.einstellungen?.passwortHinterlegt === true;
  const schwacherSchluessel = stand?.einstellungen?.schluesselEigenstaendig === false;

  if (laedt) {
    return <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Lädt…</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {fehler && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
          {fehler}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: 600,
        color: einsatzbereit ? '#15803D' : '#B45309',
      }}>
        {einsatzbereit ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        {einsatzbereit
          ? 'Alles hinterlegt – die Einladung kann versendet werden.'
          : `Noch nicht einsatzbereit: ${(stand?.fehlt || []).join(', ') || 'Angaben fehlen'}.`}
      </div>

      <div>
        <label style={BESCHRIFTUNG} htmlFor="einladung-url">Registrierungs-Link</label>
        <input
          id="einladung-url"
          type="url"
          value={registrierungUrl}
          onChange={(e) => setRegistrierungUrl(e.target.value)}
          placeholder="https://tools.en-power.eu/register"
          style={FELD}
        />
        <p style={HINWEIS}>
          Ziel des Knopfes „Jetzt Konto anlegen" in der Einladungsmail.
        </p>
      </div>

      <div style={{
        background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '16px 18px',
        display: 'grid', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <Mail size={13} />
          Postfach für den Versand
        </div>

        <div>
          <label style={BESCHRIFTUNG} htmlFor="einladung-absender">Absenderadresse</label>
          <input
            id="einladung-absender"
            type="email"
            value={absender}
            onChange={(e) => setAbsender(e.target.value)}
            placeholder="hello@en-power.eu"
            style={FELD}
          />
        </div>

        <div>
          <label style={BESCHRIFTUNG} htmlFor="einladung-benutzer">Postfach-Benutzername</label>
          <input
            id="einladung-benutzer"
            type="text"
            value={smtpBenutzer}
            onChange={(e) => setSmtpBenutzer(e.target.value)}
            autoComplete="off"
            placeholder="meist die Mailadresse selbst"
            style={FELD}
          />
        </div>

        <div>
          <label style={BESCHRIFTUNG} htmlFor="einladung-passwort">Postfach-Passwort</label>
          <input
            id="einladung-passwort"
            type="password"
            value={smtpPasswort}
            onChange={(e) => setSmtpPasswort(e.target.value)}
            autoComplete="new-password"
            placeholder={passwortDa ? '•••••••• (hinterlegt – leer lassen, um es zu behalten)' : 'Passwort eingeben'}
            style={FELD}
          />
          <p style={{ ...HINWEIS, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Lock size={11} />
            {passwortDa
              ? 'Ein Passwort ist hinterlegt. Es wird verschlüsselt gespeichert und nie wieder angezeigt.'
              : 'Wird verschlüsselt gespeichert und nie wieder angezeigt.'}
          </p>
        </div>

        {schwacherSchluessel && (
          <div style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12, padding: '9px 12px', borderRadius: 8 }}>
            Der eigene Schlüssel fehlt auf dem Server. Das Passwort wird zwar verschlüsselt,
            Schloss und Schlüssel liegen aber beieinander – das schützt nur vor zufälligem Mitlesen.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        <Knopf onClick={speichern} disabled={speichert} variante="haupt" icon={Save}>
          {speichert ? 'Speichert…' : 'Speichern'}
        </Knopf>

        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={BESCHRIFTUNG} htmlFor="einladung-test">Testmail an</label>
          <input
            id="einladung-test"
            type="email"
            value={testEmpfaenger}
            onChange={(e) => setTestEmpfaenger(e.target.value)}
            style={FELD}
          />
        </div>

        {/* Zwei Mails, zwei Empfänger - beide müssen sich einzeln prüfen lassen. */}
        <Knopf onClick={() => testen('einladung')} disabled={Boolean(testet) || !einsatzbereit} icon={Send}>
          {testet === 'einladung' ? 'Sendet…' : 'Einladung testen'}
        </Knopf>

        <Knopf onClick={() => testen('begruessung')} disabled={Boolean(testet) || !einsatzbereit} icon={Send}>
          {testet === 'begruessung' ? 'Sendet…' : 'Begrüßung testen'}
        </Knopf>
      </div>

      <p style={{ ...HINWEIS, marginTop: -4 }}>
        Beim Erreichen von „IT eingerichtet" gehen zwei Mails raus: die <strong>Einladung</strong> an
        die neue EN:POWER-Adresse aus dem Deal und die <strong>Begrüßung</strong> mit dem
        Einrichtungsvideo an die Adresse des Kontakts. Beide Testmails nehmen denselben Weg wie
        der Ernstfall, tragen aber „[Test]" im Betreff und hinterlassen keinen Vermerk in einem Deal.
      </p>

      {stand?.einstellungen?.aktualisiertAm && (
        <p style={HINWEIS}>
          Zuletzt geändert am {new Date(stand.einstellungen.aktualisiertAm).toLocaleString('de-DE')}
          {stand.einstellungen.aktualisiertVon ? ` von ${stand.einstellungen.aktualisiertVon}` : ''}.
        </p>
      )}
    </div>
  );
}
